// ---------------------------------------------------------------------------
// UpdateReadyNotice — when a freshly-deployed build is detected (the service
// worker has a new version WAITING), show a small toast inviting the user to
// reload into it. This is the missing half of the "prompt" update strategy:
// without it, a new worker sits idle until every tab is closed, so a plain
// refresh keeps serving the OLD bundle — the stale-cache trap that made
// shipped changes look like they "weren't there."
//
// The reload is user-initiated (a tap), so we never yank the page out from
// under someone mid-session — the exact problem the old auto-update had.
// ---------------------------------------------------------------------------

import { useEffect, useState } from "react";
import { Sparkles, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

const UPDATE_READY_EVENT = "pleasant:update-ready";

// The SW updater, captured in main.tsx once registration resolves. Calling it
// with `true` tells the waiting worker to take over, then reloads the page.
let doUpdate: (() => void) | null = null;

/** Wire the real updater (from registerSW). Called once from main.tsx. */
export const setUpdateHandler = (fn: () => void) => {
  doUpdate = fn;
};

/** Fire the toast. Called from the SW's onNeedRefresh callback. */
export const markUpdateReady = () => {
  try {
    window.dispatchEvent(new Event(UPDATE_READY_EVENT));
  } catch {
    /* ignore */
  }
};

export const UpdateReadyNotice = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const onReady = () => setShow(true);
    window.addEventListener(UPDATE_READY_EVENT, onReady);
    return () => window.removeEventListener(UPDATE_READY_EVENT, onReady);
  }, []);

  if (!show) return null;

  const reload = () => {
    if (doUpdate) doUpdate();
    else window.location.reload();
  };

  return (
    <div
      role="alertdialog"
      aria-label="A new version is ready"
      className="fixed inset-x-0 z-[90] flex justify-center px-4 pointer-events-none"
      style={{ bottom: "calc(env(safe-area-inset-bottom) + 5rem)" }}
    >
      <div className="pointer-events-auto max-w-sm w-full rounded-2xl border border-primary/30 bg-background/95 backdrop-blur shadow-elevated p-3.5 text-foreground">
        <div className="flex items-start gap-2.5">
          <Sparkles className="w-5 h-5 mt-0.5 shrink-0 text-primary" aria-hidden="true" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold">A newer version is ready</div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Reload to get the latest updates.
            </p>
            <div className="flex items-center gap-2 mt-2">
              <Button size="sm" className="h-8 text-xs" onClick={reload}>
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
                Reload
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 text-xs"
                onClick={() => setShow(false)}
              >
                Later
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
